/**
 * Pill — nowrap-safe presentational status/category chip.
 *
 * Use when: rendering a non-interactive status, tag, filter label, or
 * category indicator (e.g. "Assigned to me", "SLA at risk", "standard").
 * For interactive chips (clickable, dismissible) use `Chip` instead. For
 * numeric counts (with overflow `99+`) use `Counter`. For semantic status
 * with built-in icons use `StatusBadge`.
 *
 * Pill is **purely presentational** — Fluent's `Badge` is a `<span>`, not
 * a button, so attaching `onClick` here would create a click target without
 * keyboard focus, role="button", or `aria-pressed`. That's an a11y bug.
 * This component therefore intentionally does NOT accept `onClick`.
 */
import type { ReactElement, ReactNode } from 'react';
import { Badge, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

type PillAppearance = 'tint' | 'outline' | 'filled' | 'ghost';
type PillColor = 'brand' | 'danger' | 'warning' | 'success' | 'informative' | 'important' | 'subtle';
type PillSize = 'small' | 'medium' | 'large' | 'extra-large' | 'tiny' | 'extra-small';

const useStyles = makeStyles({
  pill: {
    whiteSpace: 'nowrap',
    flexShrink: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: tokens.fontWeightSemibold,
  },
});

interface PillProps {
  children: ReactNode;
  appearance?: PillAppearance;
  color?: PillColor;
  size?: PillSize;
  icon?: ReactElement;
  className?: string;
}

export function Pill({
  children,
  appearance = 'tint',
  color = 'informative',
  size = 'medium',
  icon,
  className,
}: PillProps) {
  const styles = useStyles();
  return (
    <Badge
      appearance={appearance}
      color={color}
      size={size}
      icon={icon}
      className={mergeClasses(styles.pill, className)}
    >
      {children}
    </Badge>
  );
}
